import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../medieval-office-kit';

/**
 * HEARTH HALL (Очажный зал) — the central dominant concourse of Castle Hearthhold (ADR-0051): feast hall,
 * throne, and trophy hall in one. The brightest warm-light pool on the map is the colossal GREAT HEARTH
 * dead-centre — the gather magnet every door faces. A royal blue-and-gold RUNNER draws the ceremonial axis
 * from the portcullis (east) to the DAIS + communal THRONE against the west wall (the "Seat of the Guild",
 * never a watch-post). Two long TRESTLE feast tables flank the hearth; a growing TROPHY WALL of release
 * shields lines the north wall by the dais; a colonnade of pillars + heraldic banners + braziers frame it.
 *
 * Geometry: zone (40,400) 1300×440 → centre (690,620). Doors on the N wall (x170/430/690/950/1210), the
 * S wall (x200/520/830/1160) and the E wall (vault y510, portcullis y730) are kept clear.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art, f } = ctx;
  const cx = 690;
  const cy = 620;

  // ── Floor: the ceremonial runner the length of the hall (laid first, under everything). ──
  place(ctx, kit.runnerRug(art, 1230, 120), cx, cy);

  // ── The DAIS + communal throne against the WEST wall, the heraldic rose window glowing behind it,
  //    braziers flanking it (placed below) + tall bay laurels at the outer dais corners. ──
  place(ctx, kit.roseWindow(art, 64), 92, 600);
  place(ctx, f.plant(art, 'tall'), 276, 548);
  place(ctx, f.plant(art, 'tall'), 276, 694);
  place(ctx, kit.banner(art, undefined, 120), 60, 470);
  place(ctx, kit.banner(art, 0x2f4a8a, 120), 60, 716);
  place(ctx, kit.throne(art), 122, 620);

  // ── The two trestle FEAST tables flanking the hearth, each with a bench on either long side. ──
  for (const ty of [500, 740]) {
    place(ctx, kit.feastTable(art, 212), cx, ty);
    place(ctx, kit.pew(art, 150), cx, ty - 30); // north-side bench
    place(ctx, kit.pew(art, 150), cx, ty + 30); // south-side bench
  }

  // ── The GREAT HEARTH — dead-centre, the brightest pool + the gather heart. ──
  place(ctx, kit.greatHearth(art), cx, cy);

  // ── Colonnade pillars down each long side framing the runner. ──
  for (const [px, py] of [
    [300, 458],
    [300, 782],
    [1090, 458],
    [1090, 782],
  ] as const) {
    place(ctx, kit.pillar(art), px, py);
  }
  // Braziers: flanking the THRONE/dais + in the far corners — NEVER on the central runner lane (the great
  // hearth is the only light on the spine), so nothing rivals the hearth or blocks the walking path.
  for (const [bx, by] of [
    [216, 556],
    [216, 690],
    [1170, 470],
    [1170, 770],
  ] as const) {
    place(ctx, kit.brazier(art), bx, by);
  }
  place(ctx, kit.chandelier(art), 430, 500);
  place(ctx, kit.chandelier(art), 950, 740);

  // ── The growing TROPHY WALL (release shields) along the north wall by the dais + a few heraldic banners. ──
  for (const tx of [270, 340, 560, 620]) {
    place(ctx, kit.trophyShield(art, tx % 120 === 0 ? 0x8a2f2f : undefined), tx, 432);
  }
  place(ctx, kit.banner(art, 0x8a2f2f, 96), 820, 410);
  place(ctx, kit.banner(art, undefined, 96), 1090, 410);

  // ── Wall torches on the long walls (kept off the door mouths) — extra warm edge light. ──
  for (const [tx, ty] of [
    [480, 410],
    [900, 410],
    [480, 832],
    [900, 832],
  ] as const) {
    place(ctx, kit.wallTorch(art), tx, ty);
  }
};

export default decorate;
